import { useState } from 'react'

import { useSesion } from '@/modules/auth/useSesion'
import { useDecidirTraslado, useTraslados } from '@/modules/programacion/useHerramientas'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Tarjeta } from '@/shared/ui/Tarjeta/Tarjeta'

import estilos from './HerramientasEnCalle.module.css'

/**
 * Traslados de herramienta pendientes de aprobar.
 *
 * La herramienta no cambia de manos sola: alguien lo solicita y GERENCIA lo
 * aprueba aquí. Cada tarjeta dice qué herramienta, de qué orden u operario a
 * cuál, y por qué. Desde 0122 el destino puede ser una PERSONA y no una orden
 * —el técnico que mañana arranca con ella—, y entonces en el «a dónde» va el
 * nombre en vez del número de orden. Solo la ve —y decide— quien puede
 * aprobar; para los demás no aparece.
 */
export function TrasladosPendientes() {
  const { puede } = useSesion()
  const { traslados } = useTraslados()
  const decidir = useDecidirTraslado()

  const [rechazando, setRechazando] = useState<string | null>(null)
  const [notas, setNotas] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Por el PERMISO, no por el nombre del rol. Nombrarlos a mano dejaba fuera a
  // gerencia —que desde 0114 lo puede todo— y la base, desde 0122, también
  // pregunta por el permiso: así la pantalla y el servidor dicen lo mismo.
  const puedeAprobar = puede('herramientas', 'aprobar')
  const pendientes = traslados.filter((t) => t.estado === 'pendiente')

  if (!puedeAprobar || pendientes.length === 0) return null

  async function decidirTraslado(id: string, aprobar: boolean) {
    setError(null)
    try {
      await decidir.mutateAsync({ trasladoId: id, aprobar, notas: aprobar ? undefined : notas })
      setRechazando(null)
      setNotas('')
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo decidir el traslado.')
    }
  }

  return (
    <Tarjeta
      subtitulo="Traslados de herramienta por aprobar · la herramienta no se mueve hasta que usted decide"
      sinRelleno
      tono="viva"
    >
      {error && (
        <div className={estilos.error}>
          <Aviso tipo="error">{error}</Aviso>
        </div>
      )}

      <ul className={estilos.lista}>
        {pendientes.map((t) => (
          <li key={t.id} className={estilos.fila}>
            <div className={estilos.que}>
              <span className={estilos.nombre}>{t.herramienta}</span>
              <span className={estilos.codigo}>{t.etiqueta}</span>
            </div>

            <div className={estilos.quien}>
              <span className={estilos.persona}>
                {t.orden_origen ?? t.operario_origen ?? 'sin orden'} →{' '}
                {t.orden_destino ?? t.operario_destino ?? 'sin orden'}
              </span>
              <span className={estilos.orden}>
                {t.operario_origen ?? 'sin operario'}
                {t.operario_destino && t.operario_destino !== t.operario_origen
                  ? ` → ${t.operario_destino}`
                  : ''}
              </span>
            </div>

            <div className={estilos.cuando}>
              <span className={estilos.terminada}>{t.motivo}</span>
              {t.solicitante && <span className={estilos.codigo}>Pidió: {t.solicitante}</span>}
            </div>

            {rechazando === t.id ? (
              <div className={estilos.recibiendo}>
                <input
                  type="text"
                  className={estilos.novedad}
                  placeholder="¿Por qué se rechaza? (mínimo 5 letras)"
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  autoFocus
                />
                <Boton
                  tamano="pequeno"
                  onClick={() => void decidirTraslado(t.id, false)}
                  trabajando={decidir.isPending}
                  disabled={notas.trim().length < 5}
                >
                  Confirmar rechazo
                </Boton>
                <Boton
                  tamano="pequeno"
                  variante="secundario"
                  onClick={() => {
                    setRechazando(null)
                    setNotas('')
                  }}
                >
                  Cancelar
                </Boton>
              </div>
            ) : (
              <div className={estilos.acciones}>
                <Boton
                  tamano="pequeno"
                  onClick={() => void decidirTraslado(t.id, true)}
                  trabajando={decidir.isPending}
                >
                  Aprobar
                </Boton>
                <Boton
                  tamano="pequeno"
                  variante="secundario"
                  onClick={() => {
                    setRechazando(t.id)
                    setNotas('')
                    setError(null)
                  }}
                >
                  Rechazar
                </Boton>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Tarjeta>
  )
}
